import React, { useEffect, useState } from "react";
import GenericTable from "../GenericTable/GenericTable";
import { CircularProgress, Alert, Snackbar } from "@mui/material";
import config from "../../../config";

const ordersColumns = [
  {
    id: "order_id",
    label: "Order ID",
    render: (row) => row.order_id ?? "N/A"
  },
  {
    id: "customer_name",
    label: "Customer Name",
    render: (row) => row.customer_name ?? "N/A"
  },
  {
    id: "product_names",
    label: "Product Name(s)",
    noWrap: false,
    maxWidth: "250px",
    render: (row) =>
      Array.isArray(row.product_names)
        ? (row.product_names.length > 0 ? row.product_names.join(", ") : "N/A")
        : (row.product_names ?? "N/A")
  },
  {
    id: "order_amount",
    label: "Order Amount",
    render: (row) =>
      row.order_amount != null
        ? `$ ${Number(row.order_amount).toLocaleString()}`
        : "N/A"
  },
  {
    id: "bude_order_amount",
    label: "Bud-i Amount",
    render: (row) =>
      row.bude_order_amount != null
        ? `$ ${Number(row.bude_order_amount).toLocaleString()}`
        : "N/A"
  },
  {
    id: "store_name",
    label: "Store Name",
    render: (row) => row.store_name ?? "N/A"
  },
  {
    id: "order_date",
    label: "Order Date",
    render: (row) => {
      if (!row.order_date) return "N/A";
      const d = new Date(row.order_date);
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      const year = d.getFullYear();
      return `${month}-${day}-${year}`;
    }
  },
  {
    id: "product_category",
    label: "Product Category",
    render: (row) => row.product_category ?? "N/A"
  },
  {
    id: "brand",
    label: "Brand",
    render: (row) => row.brand ?? "N/A"
  }
];


function OrdersList() {
  const [loading, setLoading] = useState(true);
  const [ordersData, setOrdersData] = useState([]);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [lastSyncTime, setLastSyncTime] = useState(null); // ✅ Store last sync timestamp

  // Get client_id from localStorage (adjust based on your auth implementation)

  const clientId = config.clientId || "0486bc6d-6d15-4ea3-8504-adefe1512324";

  useEffect(() => {
    fetchOrdersData();
  }, []);

  const fetchOrdersData = async () => {
    setLoading(true);
    setError(null);

    try {
      console.log("🔍 Fetching orders data for client:", clientId);

      const response = await fetch(
        `${config.API_BASE_URL}/client/revenue/list?client_id=${clientId}`,
        {
          method: "GET",
          headers: config.getHeaders()
        }
      );

      const result = await response.json();
      console.log("📊 Orders API response:", result);

      if (result.success && result.data) {
        setOrdersData(result.data);
        console.log(`✅ Loaded ${result.data.length} orders records`);
      } else {
        setError(result.message || "Failed to fetch orders data");
        console.error("❌ Orders fetch failed:", result.message);
      }

      // ✅ Fetch last sync time for revenue
      try {
        const batchUrl = `${config.API_BASE_URL}/batch/recent?client_id=${clientId}&limit=1`;
        const batchRes = await fetch(batchUrl, {
          headers: config.getHeaders()
        });
        const batchJson = await batchRes.json();

        if (batchRes.ok && batchJson.success && batchJson.data && batchJson.data.length > 0) {
          const lastBatch = batchJson.data[0];
          // Check if it's a revenue or both sync
          if (lastBatch.batch_type === 'revenue' || lastBatch.batch_type === 'both') {
            if (lastBatch.finished_at) {
              setLastSyncTime(lastBatch.finished_at);
              console.log("📅 Last revenue sync time loaded:", lastBatch.finished_at);
            }
          }
        }
      } catch (batchErr) {
        console.warn("⚠️ Could not fetch last sync time:", batchErr);
      }

    } catch (err) {
      console.error("❌ Error fetching orders data:", err);
      setError("Failed to fetch orders data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle revenue sync for all stores
  const handleSyncRevenue = async () => {
    try {
      setSyncing(true);

      if (!clientId) {
        setSnackbar({
          open: true,
          message: "Client ID not found",
          severity: "error"
        });
        return;
      }

      console.log("🔄 Starting revenue sync for all stores...");

      // Call batch sync API with query parameters
      const url = new URL(`${config.API_BASE_URL}/batch/sync/client`);
      url.searchParams.append('client_id', clientId);
      url.searchParams.append('batch_type', 'revenue');

      const response = await fetch(url.toString(), {
        method: "POST",
        headers: config.getHeaders()
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || "Failed to start revenue sync");
      }

      console.log("✅ Revenue sync started:", result);

      // ✅ Store last sync timestamp from response (from details array)
      if (result.details && result.details.length > 0 && result.details[0].finished_at) {
        setLastSyncTime(result.details[0].finished_at);
        console.log("📅 Last sync time updated:", result.details[0].finished_at);

        setSnackbar({
          open: true,
          message: "Orders synced successfully!",
          severity: "success"
        });

        // ✅ Refresh notifications immediately after successful sync
        console.log("🔔 Dispatching notification refresh event...");
        window.dispatchEvent(new CustomEvent('refreshNotifications'));

        // ✅ Refresh orders data to show newly synced revenue
        console.log("🔄 Refreshing orders data...");
        await fetchOrdersData();
      } else {
        console.warn("⚠️ No finished_at in response. result.details:", result.details);
        setSnackbar({
          open: true,
          message: "Sync failed to complete",
          severity: "warning"
        });
      }

    } catch (error) {
      console.error("Orders sync error:", error);
      setSnackbar({
        open: true,
        message: error.message || "Failed to start orders sync",
        severity: "error"
      });
    } finally {
      setSyncing(false);
    }
  };

  // ✅ Format last sync time for display as MM-DD-YYYY HH:MM:SS
  const formatLastSync = (isoString) => {
    if (!isoString) return null;
    const date = new Date(isoString);
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    return `${month}-${day}-${year} ${hours}:${minutes}:${seconds}`;
  };

  // 🔥 Custom Header Button: Sync Revenue with Last Sync Time
  const getSyncButtonLabel = () => {
    if (syncing) return "Syncing...";
    if (lastSyncTime) {
      const formatted = formatLastSync(lastSyncTime);
      return `Sync Orders (Last: ${formatted})`;
    }
    return "Sync Orders";
  };

  const customHeaderButtons = [
    {
      label: getSyncButtonLabel(),
      icon: "sync",
      onClick: handleSyncRevenue,
      disabled: syncing
    }
  ];

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", marginTop: "2rem" }}>
        <CircularProgress />
      </div>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <>
      <GenericTable
        title="Orders List"
        columns={ordersColumns}
        rows={ordersData}
        showView={false}
        showEdit={false}
        showDelete={false}
        dropdownFilters={["store_name", "product_category", "brand"]}
        dateFilters={["order_date"]}
        rangeFilters={["order_amount"]}
        customHeaderButtons={customHeaderButtons}
        onView={(row) => alert(`View user: ${row.username}`)}
        onEdit={(row) => alert(`Edit user: ${row.username}`)}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default OrdersList;
